import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, ExternalLink, Edit3, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slugs whose public routes go through <BlockRendererPage>. Editing them
// in the admin CMS DOES reflect on the public site. When you migrate a new
// page to render from the CMS, add its slug here.
//
// Every other registered slug is a "phantom edit" page — its admin editor
// exists but the public route still renders from a hardcoded .jsx file, so
// admin edits won't appear on the live site until the page is migrated.
const CMS_RENDERED_SLUGS = new Set([
  // Legal / policy pages
  "privacy", "terms", "security", "accessibility",
  // Product detail pages (composite solution_detail_page block)
  "advance", "aether",
  // Role/solution detail pages (composite solution_detail_page block)
  "solutions-distributors", "solutions-suppliers", "solutions-decorators",
  "solutions-print-shops", "solutions-sign-companies", "solutions-quickbooks",
  // Homepage (multi-block composition)
  "home",
  // /exchange/user-groups → composite user_groups_page block
  "user-groups",
  // /developers → composite api_docs_page block
  "developers",
  // /solutions → composite solutions_landing_page block
  "solutions",
  // /products → composite products_landing_page block
  "products",
  // /exchange → composite exchange_page block
  "exchange",
  // /exchange/elite-supplier → composite elite_supplier_page block
  "elite-supplier",
  // /academy → composite academy_page block
  "academy",
  // /integrations → composite integrations_page block
  "integrations",
  // /events, /openhouse, /customer-portal → composite blocks
  "events",
  "openhouse",
  "customer-portal",
  // Careers — CareersPage.jsx fetches this slug's blocks via API
  "careers-intro",
]);

// Slugs that are editable but via a different admin surface. For these the
// list shows a blue "Site Settings" pill and clicking Edit still opens the
// block editor (which will show a friendly redirect banner via
// AdminSitePageEditor's PhantomEditWarning).
const SITE_SETTINGS_SLUGS = new Set(["contact"]);

function CmsStatusBadge({ slug }) {
  if (SITE_SETTINGS_SLUGS.has(slug)) {
    return (
      <a
        href="/admin/site-settings"
        data-testid={`admin-site-pages-cms-status-${slug}`}
        title="This page's content is edited via Site Settings, not through blocks. Click to open Site Settings."
        className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-sky-700 hover:bg-sky-100"
      >
        <CheckCircle2 className="h-2.5 w-2.5" /> Site Settings
      </a>
    );
  }
  const isCms = CMS_RENDERED_SLUGS.has(slug);
  if (isCms) {
    return (
      <span
        data-testid={`admin-site-pages-cms-status-${slug}`}
        title="This page renders from the CMS. Edits here reflect on the live site."
        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700"
      >
        <CheckCircle2 className="h-2.5 w-2.5" /> CMS-driven
      </span>
    );
  }
  return (
    <span
      data-testid={`admin-site-pages-cms-status-${slug}`}
      title="This page still renders from hardcoded page code. Edits here save to the database but WON'T appear on the live site until the page is migrated to the CMS."
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800"
    >
      <AlertTriangle className="h-2.5 w-2.5" /> Code-driven
    </span>
  );
}

/**
 * Lists every built-in marketing page that uses the block-based CMS. Each row
 * links to the block editor for that page.
 */
export default function AdminSitePagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/site-pages`);
        setPages(data || []);
      } catch {
        toast.error("Could not load site pages.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="admin-site-pages" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2 text-slate-900">
          <FileText className="h-4 w-4 text-sky-600" />
          <h1 className="text-base font-bold">Site pages</h1>
          <span className="text-xs text-slate-400">({pages.length})</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Built-in marketing pages you can edit as a sequence of blocks. Add and reorder Hero,
          Two-column, and CTA-banner sections on any page.
        </p>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : pages.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">No site pages registered yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-site-pages-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Public URL</th>
                  <th className="text-left px-4 py-2.5 font-semibold">CMS status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Last updated</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {pages.map((p) => (
                  <tr
                    key={p.slug}
                    data-testid={`admin-site-pages-row-${p.slug}`}
                    className="hover:bg-slate-50/50"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{p.title}</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-[11px] font-mono text-slate-500">
                      {p.public_path}
                    </td>
                    <td className="px-4 py-3">
                      <CmsStatusBadge slug={p.slug} />
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500">
                      {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <a
                          href={p.public_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on site"
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <Link
                          to={`/admin/site-pages/${p.slug}`}
                          data-testid={`admin-site-pages-edit-${p.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
